import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft, Percent, ShieldCheck, Building2, Pencil, Save, X, Users, Info, Loader2,
  ChevronLeft, ChevronRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Arbeitnehmeranteile (Abzüge)
const EMPLOYEE_META = [
  { key: 'ahv_iv_eo_rate', code: 'AHV / IV / EO', desc: 'Alters-, Invaliden- und Erwerbsersatz (Arbeitnehmer)' },
  { key: 'alv_rate', code: 'ALV', desc: 'Arbeitslosenversicherung' },
  { key: 'nbuv_rate', code: 'NBUV', desc: 'Nichtberufsunfallversicherung' },
  { key: 'uvgz_rate', code: 'UVGZ', desc: 'Unfall-Zusatzversicherung' },
  { key: 'ktg_rate', code: 'KTG', desc: 'Krankentaggeldversicherung' },
];
// Arbeitgeberanteile (AG)
const EMPLOYER_META = [
  { key: 'ahv_iv_eo_ag_rate', code: 'AHV / IV / EO (AG)', desc: 'Arbeitgeberanteil (i.d.R. = Arbeitnehmeranteil)' },
  { key: 'alv_ag_rate', code: 'ALV (AG)', desc: 'Arbeitgeberanteil Arbeitslosenversicherung' },
];
const ALL_KEYS = [...EMPLOYEE_META, ...EMPLOYER_META].map((r) => r.key);

const fmtPct = (n) => `${(Number(n) || 0).toLocaleString('de-CH', { maximumFractionDigits: 3 })} %`;

const PayrollDeductionsPage = () => {
  const navigate = useNavigate();
  const nowYear = new Date().getFullYear();
  const [year, setYear] = useState(nowYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setEditing(false);
    try {
      const res = await axios.get(`${API}/timetracking/payroll/year-rates?year=${year}`, { headers: headers() });
      setData(res.data);
      setForm(res.data.rates || {});
    } catch (e) {
      toast.error('Abzugssätze konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => { setForm(data?.rates || {}); setEditing(true); };
  const cancelEdit = () => { setForm(data?.rates || {}); setEditing(false); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      ALL_KEYS.forEach((k) => { payload[k] = parseFloat(form[k]) || 0; });
      await axios.put(`${API}/timetracking/payroll/year-rates?year=${year}`, payload, { headers: headers() });
      toast.success(`Sätze für ${year} gespeichert`);
      setEditing(false);
      await load();
    } catch (e) {
      toast.error('Fehler: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const source = data?.rates?.source;
  const inheritedFrom = data?.rates?.year;
  const yearOptions = Array.from(
    new Set([...(data?.available_years || []), nowYear + 1, nowYear, nowYear - 1, nowYear - 2, nowYear - 3])
  ).sort((a, b) => b - a);

  const renderRow = (r, group) => {
    const cur = data.rates?.[r.key];
    return (
      <tr key={r.key} className="border-b border-gray-50" data-testid={`rate-row-${r.key}`}>
        <td className="py-2 pr-3 font-semibold text-gray-900">{r.code}</td>
        <td className="py-2 px-2 text-gray-600">{r.desc}</td>
        <td className="py-2 pl-2 text-right">
          {editing ? (
            <div className="flex items-center justify-end gap-1">
              <Input
                type="number" step="0.001" min="0"
                value={form[r.key] ?? ''}
                onChange={(e) => setForm({ ...form, [r.key]: e.target.value })}
                className="w-24 text-right"
                data-testid={`rate-input-${r.key}`}
              />
              <span className="text-gray-400">%</span>
            </div>
          ) : (
            <span className="font-semibold text-gray-900" data-testid={`rate-value-${r.key}`}>{fmtPct(cur)}</span>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-6" data-testid="payroll-deductions-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="deductions-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sozialabgaben &amp; Lohnabzüge</h1>
            <p className="text-sm text-gray-500">Hinterlegte Prozentsätze pro Gültigkeitsjahr · Jahresend-Kontrolle</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Jahr-Auswahl */}
          <div className="flex items-center gap-1" data-testid="year-selector">
            <Button variant="outline" size="icon" onClick={() => { setYear((y) => y - 1); }} data-testid="year-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm font-semibold"
              data-testid="year-select"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <Button variant="outline" size="icon" onClick={() => { setYear((y) => y + 1); }} data-testid="year-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          {!loading && !editing && (
            <Button onClick={startEdit} data-testid="deductions-edit-btn">
              <Pencil className="w-4 h-4 mr-1" /> Bearbeiten
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-100 p-3 text-sm text-blue-800" data-testid="deductions-erp-note">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          Die <strong>effektiven Abzüge</strong> berechnet das externe ERP. Hier pflegst du die
          <strong> hinterlegten Prozentsätze pro Jahr</strong> – so bleibt bei der Jahresend-Kontrolle nachvollziehbar,
          welche Sätze in welchem Jahr galten.
        </span>
      </div>

      {loading ? (
        <Card><CardContent className="py-12 flex items-center justify-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Wird geladen…
        </CardContent></Card>
      ) : (
        <>
          {source !== 'exact' && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="year-inherited-banner">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Für <strong>{year}</strong> ist noch kein eigener Eintrag hinterlegt – es gelten{' '}
                {source === 'inherited' ? <>die Sätze von <strong>{inheritedFrom}</strong></> : <>die CH-Standardsätze</>}.
                Mit „Bearbeiten → Speichern" legst du einen eigenen Eintrag für {year} an.
              </span>
            </div>
          )}

          {/* Arbeitnehmeranteile */}
          <Card data-testid="employee-rates-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Percent className="w-5 h-5 text-gray-700" /> Arbeitnehmeranteile (Abzüge) · {year}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[480px]" data-testid="employee-rates-table">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-100">
                      <th className="py-2 pr-3 font-medium text-left">Abzug</th>
                      <th className="py-2 px-2 font-medium text-left">Beschreibung</th>
                      <th className="py-2 pl-2 font-medium text-right">Satz</th>
                    </tr>
                  </thead>
                  <tbody>{EMPLOYEE_META.map((r) => renderRow(r, 'employee'))}</tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Arbeitgeberanteile */}
          <Card data-testid="employer-rates-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Building2 className="w-5 h-5 text-gray-700" /> Arbeitgeberanteile (AG) · {year}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[480px]" data-testid="employer-rates-table">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-100">
                      <th className="py-2 pr-3 font-medium text-left">Beitrag</th>
                      <th className="py-2 px-2 font-medium text-left">Beschreibung</th>
                      <th className="py-2 pl-2 font-medium text-right">Satz</th>
                    </tr>
                  </thead>
                  <tbody>{EMPLOYER_META.map((r) => renderRow(r, 'employer'))}</tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-400 mt-3">
                Die Arbeitgeberanteile fliessen als Kosten in die <strong>Vollkosten-Marge</strong> der
                Trainer-Rentabilität ein (AHV/ALV-AG auf den Bruttolohn + PK pro Mitarbeiter).
              </p>
              {editing && (
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" onClick={cancelEdit} disabled={saving} data-testid="deductions-cancel-btn">
                    <X className="w-4 h-4 mr-1" /> Abbrechen
                  </Button>
                  <Button onClick={save} disabled={saving} data-testid="deductions-save-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Für {year} speichern
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pro Mitarbeiter */}
          <Card data-testid="per-trainer-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="w-5 h-5 text-gray-700" /> Individuell pro Mitarbeiter
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg border border-gray-100 p-3">
                  <p className="font-semibold text-gray-900">PK / BVG (Arbeitgeber)</p>
                  <p className="text-sm text-gray-600">Fixbetrag CHF/Monat – pro Mitarbeiter einstellbar, fliesst in die Vollkosten.</p>
                </div>
                <div className="rounded-lg border border-gray-100 p-3">
                  <p className="font-semibold text-gray-900">BVG / PK (Abzug)</p>
                  <p className="text-sm text-gray-600">Arbeitnehmer-Fixbetrag CHF – pro Mitarbeiter.</p>
                </div>
                <div className="rounded-lg border border-gray-100 p-3">
                  <p className="font-semibold text-gray-900">Quellensteuer</p>
                  <p className="text-sm text-gray-600">Prozentsatz – nur falls quellensteuerpflichtig.</p>
                </div>
              </div>
              <Button variant="outline" onClick={() => navigate('/staff')} data-testid="deductions-to-staff-btn">
                <Users className="w-4 h-4 mr-1" /> Zu den Mitarbeitenden
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default PayrollDeductionsPage;
